import type { DataRow } from "../types";
import { reportedDate, reportedDateTime } from "./globalDateRange";

export type InfluenceSourceType = "station" | "vc";
export type FactorStatus = "equal-default" | "actual";

export type InfluenceSource = {
  name: string;
  type: InfluenceSourceType;
  slot: number;
  rawFactor: number;
  normalizedFactor: number;
  factorStatus: FactorStatus;
};

export type DmaInfluenceRecord = {
  branchNumber: string;
  dmaCode: string;
  sources: InfluenceSource[];
  pressurePoint: string | null;
  pressureTargets: { night: number | null; day: number | null; late: number | null };
};

export type SourceInfluenceSnapshot = {
  schemaVersion: 1;
  versionId: string;
  versionLabel: string;
  generatedAt: string;
  provenance: {
    spreadsheetUrl: string;
    spreadsheetId: string;
    sheetTitle: string;
    sheetId: string;
    sourceChecksum: string;
  };
  factorMode: FactorStatus;
  counts: {
    dma: number;
    relations: number;
    sources: number;
    missingSource: number;
    multiSource: number;
    pressurePoints: number;
  };
  records: DmaInfluenceRecord[];
};

export type InfluenceFilters = { workTypes: string[]; dateFrom: string; dateTo: string };

export type SourceMetric = {
  name: string;
  type: InfluenceSourceType;
  dmaCount: number;
  branchCount: number;
  pressurePointCount: number;
  relatedJobs: number;
  allocatedJobs: number;
  byWorkType: Record<string, { related: number; allocated: number }>;
  byDayType: Record<"weekday" | "saturday" | "sunday" | "unknown", { related: number; allocated: number }>;
  byHour: { related: number; allocated: number }[];
};

export type InfluenceAnalysis = {
  metrics: SourceMetric[];
  filteredJobs: number;
  mappedJobs: number;
  unmappedJobs: number;
  allocatedTotal: number;
};

export function normalizeDmaCode(value: unknown): string {
  const text = String(value ?? "").trim();
  const digits = text.replace(/\D/g, "");
  return digits ? digits.padStart(6, "0").slice(-6) : "";
}

function jobKey(row: DataRow): string {
  return String(row.jobId ?? row.__rowId);
}

function dayType(value: string | null | undefined): "weekday" | "saturday" | "sunday" | "unknown" {
  if (!value) return "unknown";
  const parsed = new Date(`${value.slice(0, 10)}T12:00:00+07:00`);
  if (Number.isNaN(parsed.getTime())) return "unknown";
  const day = parsed.getDay();
  return day === 0 ? "sunday" : day === 6 ? "saturday" : "weekday";
}

function hourOf(row: DataRow): number | null {
  const text = String(reportedDateTime(row) ?? "");
  const match = text.match(/(?:T|\s|^)(\d{1,2}):\d{2}/);
  const hour = match ? Number(match[1]) : Number.NaN;
  return Number.isInteger(hour) && hour >= 0 && hour <= 23 ? hour : null;
}

function uniqueJobs(rows: DataRow[], filters: InfluenceFilters): DataRow[] {
  const seen = new Set<string>();
  return rows.filter((row) => {
    if (filters.workTypes.length && !filters.workTypes.includes(row.workType || "ไม่ระบุ")) return false;
    const date = reportedDate(row);
    if (filters.dateFrom && (!date || date < filters.dateFrom)) return false;
    if (filters.dateTo && (!date || date > filters.dateTo)) return false;
    const key = jobKey(row);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

export function buildInfluenceAnalysis(snapshot: SourceInfluenceSnapshot, rows: DataRow[], filters: InfluenceFilters): InfluenceAnalysis {
  const recordsByDma = new Map(snapshot.records.map((record) => [normalizeDmaCode(record.dmaCode), record]));
  const sourceRecords = new Map<string, DmaInfluenceRecord[]>();
  for (const record of snapshot.records) {
    for (const source of record.sources) {
      const list = sourceRecords.get(source.name) ?? [];
      list.push(record);
      sourceRecords.set(source.name, list);
    }
  }
  const sourceTypes = new Map<string, InfluenceSourceType>();
  for (const record of snapshot.records) for (const source of record.sources) sourceTypes.set(source.name, source.type);
  const metricsByName = new Map<string, SourceMetric>();
  for (const [name, records] of sourceRecords) {
    metricsByName.set(name, {
      name,
      type: sourceTypes.get(name) ?? "station",
      dmaCount: new Set(records.map((record) => record.dmaCode)).size,
      branchCount: new Set(records.map((record) => record.branchNumber)).size,
      pressurePointCount: new Set(records.map((record) => record.pressurePoint).filter(Boolean)).size,
      relatedJobs: 0,
      allocatedJobs: 0,
      byWorkType: {},
      byDayType: { weekday: { related: 0, allocated: 0 }, saturday: { related: 0, allocated: 0 }, sunday: { related: 0, allocated: 0 }, unknown: { related: 0, allocated: 0 } },
      byHour: Array.from({ length: 24 }, () => ({ related: 0, allocated: 0 })),
    });
  }

  const jobs = uniqueJobs(rows, filters);
  let mappedJobs = 0;
  let allocatedTotal = 0;
  for (const row of jobs) {
    const record = recordsByDma.get(normalizeDmaCode(row.dmaCode));
    if (!record?.sources.length) continue;
    mappedJobs += 1;
    const kind = dayType(reportedDate(row));
    const hour = hourOf(row);
    const workType = String(row.workType || "ไม่ระบุ");
    for (const source of record.sources) {
      const metric = metricsByName.get(source.name);
      if (!metric) continue;
      const weight = source.normalizedFactor;
      metric.relatedJobs += 1;
      metric.allocatedJobs += weight;
      metric.byWorkType[workType] ??= { related: 0, allocated: 0 };
      metric.byWorkType[workType].related += 1;
      metric.byWorkType[workType].allocated += weight;
      metric.byDayType[kind].related += 1;
      metric.byDayType[kind].allocated += weight;
      if (hour !== null) {
        metric.byHour[hour].related += 1;
        metric.byHour[hour].allocated += weight;
      }
      allocatedTotal += weight;
    }
  }
  return {
    metrics: [...metricsByName.values()].sort((a, b) => b.allocatedJobs - a.allocatedJobs || a.name.localeCompare(b.name, "th")),
    filteredJobs: jobs.length,
    mappedJobs,
    unmappedJobs: jobs.length - mappedJobs,
    allocatedTotal,
  };
}

export function pressureTargetSummary(records: DmaInfluenceRecord[]) {
  const summarize = (values: (number | null)[]) => {
    const usable = values.filter((value): value is number => typeof value === "number" && Number.isFinite(value));
    if (!usable.length) return { min: null, max: null, average: null, count: 0 };
    return { min: Math.min(...usable), max: Math.max(...usable), average: usable.reduce((sum, value) => sum + value, 0) / usable.length, count: usable.length };
  };
  return {
    night: summarize(records.map((record) => record.pressureTargets.night)),
    day: summarize(records.map((record) => record.pressureTargets.day)),
    late: summarize(records.map((record) => record.pressureTargets.late)),
  };
}
